import { faCopy } from '@fortawesome/free-regular-svg-icons';
import { faCopy as faCopySolid } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Link from 'found/Link';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Button, Tooltip } from 'reactstrap';
import styled from 'styled-components';

import FlashesStore from 'src/stores/FlashesStore';

const CLOSE_FLASH_NOTIFICATION_TIMEOUT = 15000;

/**
 * Handles copying UUID to clipboard and displays success message.
 * @param {string} uuid - The UUID string to copy to clipboard
 * @param {string} successMsg - Success message to display after copying
 */
const handleCopy = (uuid, successMsg) => {
  navigator.clipboard.writeText(uuid);
  if (successMsg) {
    FlashesStore.flash(FlashesStore.SUCCESS, successMsg);
    setTimeout(() => {
      FlashesStore.reset();
    }, CLOSE_FLASH_NOTIFICATION_TIMEOUT);
  }
};

const idSuffix = Math.random().toString(36).slice(2, -1).toUpperCase();

/**
 * Styled button component for copying UUID to clipboard.
 * Extends Reactstrap Button with Bootstrap classes and custom hover behavior.
 */
const CopyButton = styled(Button).attrs({
  className: 'btn btn-link p-0',
})`
  color: inherit !important;
  --bs-btn-border-width: 0 !important;
   margin-inline-start: calc(0.1 * var(--spacer-4));

  &:hover {
    background-color: transparent !important;
  }
`;

/**
 * UUID component that displays a shortened UUID with a tooltip showing the full UUID on hover.
 * Includes a copy button that copies the full UUID to clipboard and shows a success message.
 * @param {object} props - Component props
 * @param {string} props.abbrValue - Display text for the UUID (defaults to first UUID segment)
 * @param {string} props.copyTitle - Title text for the copy button tooltip
 * @param {string} props.copySuccessMsg - Success message shown after copying UUID
 * @param {string} props.url - Optional URL to make the UUID a clickable link
 * @param {string} props.uuid - The full UUID string to display and copy
 * @returns {React.ReactElement} UUID component with copy functionality and tooltip
 */
function UUID({
  abbrValue, copyTitle, copySuccessMsg, url, uuid,
}) {
  const intl = useIntl();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  if (!uuid) {
    return null;
  }
  const finalAbbrValue = abbrValue || uuid?.split('-')[0];
  const id = `UUID-${uuid}-${idSuffix}`;
  const toggle = () => setTooltipOpen(!tooltipOpen);
  const finalCopyTitle = copyTitle || intl.formatMessage({
    id: 'uuid.copy_uuid.title',
    defaultMessage: 'Copy UUID to the clipboard',
  });
  const finalCopySuccessMsg = copySuccessMsg || intl.formatMessage({
    id: 'uuid.copy_uuid.success.message',
    defaultMessage: 'UUID copied to the clipboard.',
  });
  const copyIcon = isHovered ? faCopySolid : faCopy;
  const uuidWithAbbr = (
    <abbr style={{ textDecoration: 'underline dotted', cursor: 'help' }} id={id}>
      {finalAbbrValue}
    </abbr>
  );

  return (
    <span className="text-nowrap">
      {url ? (<Link to={url}>{uuidWithAbbr}</Link>) : uuidWithAbbr}
      <Tooltip placement="bottom" isOpen={tooltipOpen} target={id} toggle={toggle} boundariesElement="window">
        {uuid}
      </Tooltip>
      <CopyButton
        onClick={() => handleCopy(uuid, finalCopySuccessMsg)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        title={finalCopyTitle}
      >
        <FontAwesomeIcon icon={copyIcon} size="md" />
      </CopyButton>
    </span>
  );
}

UUID.propTypes = {
  abbrValue: PropTypes.string,
  copyTitle: PropTypes.string,
  copySuccessMsg: PropTypes.string,
  url: PropTypes.string,
  uuid: PropTypes.string.isRequired,
};
UUID.defaultProps = {
  abbrValue: null,
  copyTitle: null,
  copySuccessMsg: null,
  url: null,
};

export default UUID;
